import { useCallback, useSyncExternalStore } from "react";
import { GuestDetailLayout, NamedGuestDetailLayout } from "@/types/Event";
import { useEvent } from "@/lib/query/hooks/useEvent";
import { useViewsOptional } from "@/providers/ViewsProvider";
import {
  GDL_DEFAULT_VIEW_SCOPE,
  GDL_STANDARD,
  getLayoutPref,
  setLayoutPref,
  subscribeLayoutPref,
  viewScopeOf,
} from "@/lib/storage/guestDetailLayoutPref";
import { LAYOUT_FIELDS } from "./guestDetailLayout";

export type ResolvedLayoutSource = "viewPref" | "viewLink" | "defaultPref" | "standard";

type SelectArgs = {
  /** The unnamed Standard layout (Event.guestDetailLayout). */
  standard: GuestDetailLayout | undefined;
  /** The event's named layouts (Event.guestDetailLayouts). */
  named: NamedGuestDetailLayout[] | undefined;
  /** The active table view's linked layout id, if any. */
  viewLinkedId: string | undefined;
  /** User's pick scoped to the CURRENT view context (`gdl-<e>-<viewId>`). */
  viewScopedPrefId: string | null;
  /** User's pick scoped to the DEFAULT view context (`gdl-<e>-DEFAULT`). */
  defaultScopedPrefId: string | null;
};

export type SelectedLayout = {
  /** The raw layout to render (still needs resolveLayout / resolveSectionConfig).
   *  undefined = no Standard layout persisted → caller derives the default. */
  layout: GuestDetailLayout | undefined;
  /** GDL_STANDARD or the id of the selected named layout. */
  selectedId: string;
  source: ResolvedLayoutSource;
};

/**
 * Pure precedence engine for which guest-detail layout to show:
 *
 *   1. the user's pick for the CURRENT view context (a manual pick must win
 *      over the view's link, visibly, or the picker is a dead control),
 *   2. the active view's linked layout,
 *   3. the user's pick made outside any view context (DEFAULT scope — also
 *      what seating/slotting/AI-opened popups resolve, since those surfaces
 *      always run under the DEFAULT view),
 *   4. the Standard layout.
 *
 * Every id is looked up against the CURRENT named-layout list; unresolvable
 * (dangling) ids fall through silently — deleted layouts must never blank the
 * popup, and stale prefs/links are legal by design.
 */
export function selectGuestDetailLayout(args: SelectArgs): SelectedLayout {
  const named = args.named ?? [];
  const byId = (id: string) => named.find((l) => l.id === id);

  const candidates: Array<{ id: string | null | undefined; source: ResolvedLayoutSource }> = [
    { id: args.viewScopedPrefId, source: "viewPref" },
    { id: args.viewLinkedId, source: "viewLink" },
    { id: args.defaultScopedPrefId, source: "defaultPref" },
  ];

  for (const { id, source } of candidates) {
    if (!id) continue;
    if (id === GDL_STANDARD) {
      // An explicit "Standard" pick beats a view link — but only pref sources
      // can carry the sentinel (views link named layouts or nothing).
      return { layout: args.standard, selectedId: GDL_STANDARD, source };
    }
    const match = byId(id);
    if (match) return { layout: match, selectedId: match.id, source };
    // Dangling id → try the next precedence level.
  }

  return { layout: args.standard, selectedId: GDL_STANDARD, source: "standard" };
}

export type ResolvedGuestDetailLayout = SelectedLayout & {
  /** True while the event layout fields are still loading — consumers keep
   *  their existing "render nothing yet" behavior to avoid a reshuffle flash. */
  isPending: boolean;
  namedLayouts: NamedGuestDetailLayout[];
  /** Persist a pick for the current view context and re-render this tab. */
  select: (layoutId: string) => void;
};

/**
 * Shared resolution hook for ALL guest-detail layout consumers (field groups,
 * sidebar tabs, header actions, the popup's layout picker). Subscribes to the
 * same `useEvent(eventId, LAYOUT_FIELDS)` cache entry the builder writes, so
 * a save lands everywhere at once. Works inside the guest list's real
 * ViewsProvider, inside StandaloneGuestDetail's DEFAULT-seeded provider, and
 * with no provider at all (builder-side previews).
 */
export function useResolvedGuestDetailLayout(
  eventId: string | null | undefined,
): ResolvedGuestDetailLayout {
  const layoutQ = useEvent(eventId ?? null, LAYOUT_FIELDS);
  const views = useViewsOptional();
  const activeView = views?.activeViewState;
  const viewScope = viewScopeOf(activeView?.tableViewId);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!eventId) return () => {};
      // One subscription covers both scopes — the store notifies all
      // listeners on any layout-pref write in this tab, and cross-tab
      // per-key; over-notifying is harmless (getSnapshot dedupes).
      const unsubView = subscribeLayoutPref(eventId, viewScope, onStoreChange);
      const unsubDefault = subscribeLayoutPref(eventId, GDL_DEFAULT_VIEW_SCOPE, onStoreChange);
      return () => {
        unsubView();
        unsubDefault();
      };
    },
    [eventId, viewScope],
  );
  const viewScopedPrefId = useSyncExternalStore(
    subscribe,
    () => (eventId ? getLayoutPref(eventId, viewScope) : null),
  );
  const defaultScopedPrefId = useSyncExternalStore(
    subscribe,
    () => (eventId ? getLayoutPref(eventId, GDL_DEFAULT_VIEW_SCOPE) : null),
  );

  const named = layoutQ.data?.guestDetailLayouts;
  const selected = selectGuestDetailLayout({
    standard: layoutQ.data?.guestDetailLayout,
    named,
    viewLinkedId: activeView?.guestDetailLayoutId || undefined,
    viewScopedPrefId,
    defaultScopedPrefId,
  });

  const select = useCallback(
    (layoutId: string) => {
      if (!eventId) return;
      setLayoutPref(eventId, viewScope, layoutId);
    },
    [eventId, viewScope],
  );

  return {
    ...selected,
    isPending: layoutQ.isPending,
    namedLayouts: named ?? [],
    select,
  };
}
